/* ============================================================
   mazo-lottie.js: the two cart Lotties (empty-cart illustration, discount confetti), split out of mazo-drawer.js
   on 2026-10-17 (T449, 800-line rule). Same code, moved as is. It publishes one object, window.mazoCartLottie
   = { celebrate, playEmpty, mountAnchors }, which mazo-drawer.js calls. Loaded with `defer` BEFORE mazo-drawer.js
   in the same two places (snippets/cart-drawer.liquid, sections/main-cart-items.liquid), so it exists by the time
   the drawer boots. Guarded so a page that includes it twice runs it once.
   ============================================================ */
if (!window.mazoCartLottie) {
  (function () {
    'use strict';

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      /* ============================================================
         LOTTIE — the empty-cart illustration and the discount confetti.
         Both lazy: 56 KB + 181 KB of JSON and the 168 KB library never touch a page load.
         ============================================================ */

      var libPromise = null;
      var jsonCache = {};
      var instances = {};

      /* ⚠️ MORE COLOURFUL ON REQUEST. A first pass mapped everything onto two hues and read as a maroon
         smudge — on-brand but not a celebration. Six warm colours keep it reading as confetti while nothing
         cool or neon survives, which is what made the stock file look like a third-party graphic.
         Lottie stores colours as normalised RGBA under `c.k`. Done here rather than in the .lottie so the
         owner's file stays the untouched master in assets-source/.
         ⚠️ CONFETTI ONLY. The empty-cart illustration is deliberately NOT recoloured: it is artwork the owner
         supplied, and a partial remap (its blues and pinks fell outside the tolerance while its whites did
         not) looked worse than either leaving it alone or converting it properly. */
      var PALETTE = [
        [[1.0, 0.2, 0.6], '#C8892A'],
        [[1.0, 0.65, 0.23], '#B4471F'],
        [[0.24, 0.5, 1.0], '#5E1204'],
        [[0.4, 0.4, 0.4], '#E3B25F'],
        [[1.0, 0.4, 0.6], '#9C5544'],
        [[1.0, 0.745, 0.4], '#F0EDE7']
      ];

      function hexToRgb(hex) {
        return [
          parseInt(hex.slice(1, 3), 16) / 255,
          parseInt(hex.slice(3, 5), 16) / 255,
          parseInt(hex.slice(5, 7), 16) / 255
        ];
      }

      function recolour(node) {
        if (!node || typeof node !== 'object') return;
        if (Array.isArray(node)) {
          for (var i = 0; i < node.length; i++) recolour(node[i]);
          return;
        }
        if (node.c && node.c.k && Array.isArray(node.c.k) && typeof node.c.k[0] === 'number') {
          var k = node.c.k;
          for (var j = 0; j < PALETTE.length; j++) {
            var from = PALETTE[j][0];
            if (Math.abs(k[0] - from[0]) < 0.06 &&
                Math.abs(k[1] - from[1]) < 0.06 &&
                Math.abs(k[2] - from[2]) < 0.06) {
              var to = hexToRgb(PALETTE[j][1]);
              k[0] = to[0]; k[1] = to[1]; k[2] = to[2];
              break;
            }
          }
        }
        for (var key in node) {
          if (Object.prototype.hasOwnProperty.call(node, key)) recolour(node[key]);
        }
      }

      /* ⚠️⚠️ IT MUST REUSE AN EXISTING <script> TAG, NEVER ADD A SECOND ONE, AND THAT IS NOT A PERFORMANCE NICETY —
         LOADING LOTTIE TWICE MADE THE HOMEPAGE FLOWER BRANCH RENDER AS BLANK PAPER FOR EVERY VISITOR WITH AN EMPTY
         CART. Measured: `sections/mazo-quote.liquid` has its own `<script defer>` for lottie-light, and this function
         injected a second `async` copy for the empty-cart animation. Deferred scripts run after parsing, so the async
         copy won the race, `ensureLib` resolved, and then the deferred copy EXECUTED THE LIBRARY AGAIN — which resets
         lottie's internal element-id counter. Both animations then minted `id="__lottie_element_2"`, and Chrome
         resolves a duplicate id to the FIRST match in the document, so the branch's 415 paths were being clipped by
         the 200x200 cart animation's clipPath and nothing was painted.
         Proven two ways: renaming the cart animation's clipPath id in the live DOM made the branch appear instantly,
         and putting one item in the cart (so the empty-cart animation never mounts, leaving one script) also made it
         render perfectly. The branch's own JSON, scrub and geometry were all healthy the whole time — 13 distinct
         monotonic frames over a 675px scroll range — which is exactly why this survived an earlier investigation that
         verified the scrub and concluded the animation was fine.
         ⚠️ A `data-mazo-lib` flag is not enough on its own: the quote section's tag is plain Liquid and carries no
         flag, so the check has to match on the SRC filename. */
      function ensureLib(src) {
        if (window.lottie) return Promise.resolve(window.lottie);
        if (libPromise) return libPromise;
        var file = String(src).split('/').pop().split('?')[0];
        var existing = null;
        var tags = document.querySelectorAll('script[src]');
        for (var i = 0; i < tags.length; i++) {
          if (tags[i].src.indexOf(file) !== -1) { existing = tags[i]; break; }
        }
        libPromise = new Promise(function (resolve, reject) {
          if (existing) {
            /* the tag is already in the document (deferred or still loading) — wait for IT, do not add another */
            if (window.lottie) { resolve(window.lottie); return; }
            existing.addEventListener('load', function () { resolve(window.lottie); }, { once: true });
            existing.addEventListener('error', reject, { once: true });
            /* a deferred tag that has already executed fires no further events, so poll briefly as a backstop */
            var tries = 0;
            var t = setInterval(function () {
              if (window.lottie) { clearInterval(t); resolve(window.lottie); }
              else if (++tries > 60) { clearInterval(t); reject(new Error('lottie did not load')); }
            }, 100);
            return;
          }
          var el = document.createElement('script');
          el.src = src;
          el.async = true;
          el.onload = function () { resolve(window.lottie); };
          el.onerror = reject;
          document.head.appendChild(el);
        });
        return libPromise;
      }

      function fetchJson(url, doRecolour) {
        if (jsonCache[url]) return jsonCache[url];
        jsonCache[url] = fetch(url)
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (doRecolour) recolour(data.layers);
            return data;
          });
        return jsonCache[url];
      }

      function mount(key, host, opts) {
        return ensureLib(opts.lib)
          .then(function () { return fetchJson(opts.src, opts.recolour); })
          .then(function (data) {
            if (!host.isConnected) return null;   // re-rendered while we were fetching
            if (instances[key]) {
              try { instances[key].destroy(); } catch (e) {}
              instances[key] = null;
            }
            host.innerHTML = '';
            var anim = window.lottie.loadAnimation({
              container: host,
              renderer: 'svg',
              loop: !!opts.loop,
              /* autoplay:false plus an explicit goToAndPlay below — autoplay alone can leave a re-mounted
                 instance on its last frame, and the confetti must replay every time. */
              autoplay: false,
              animationData: data,
              rendererSettings: { preserveAspectRatio: 'xMidYMid meet' }
            });
            instances[key] = anim;
            anim.addEventListener('DOMLoaded', function () {
              if (opts.onReady) opts.onReady();
              anim.goToAndPlay(0, true);
            });
            return anim;
          })
          .catch(function () { /* an illustration is never worth breaking the cart for */ });
      }

      /* ⚠️ `force` IS WHAT STOPS THIS FROM LOOPING FOREVER, AND WITHOUT IT THE CONFETTI FREEZES AT FRAME 0.
         The bug the owner reported as "confetti lottie not platying .. getting stuck":
           mount() injects an <svg> into the anchor
             -> that is a childList mutation inside #CartDrawer
               -> the MutationObserver in watch() fires
                 -> mountAnchors() -> celebrate() -> destroy() + mount() again
                   -> another mutation ... forever.
         So the animation was being torn down and rebuilt continuously and never advanced past its first
         frame. `playEmpty()` never had this problem because it already returned early when its host had an
         <svg>; celebrate() had no such guard because it MUST be able to replay.
         The split: the observer calls the guarded path (mount only if unmounted), while a real drawer OPEN or
         a successful code application calls it with force=true to genuinely replay. */
      function celebrate(force) {
        if (reduce) return;   // pure decoration; the Discount ledger row already carries the information
        var host = document.querySelector('[data-mazo-confetti]');
        if (!host) return;    // Liquid renders it only when a discount is really on the cart
        if (!force && host.querySelector('svg')) return;   // already mounted; do NOT rebuild
        mount('confetti', host, {
          src: host.getAttribute('data-src'),
          lib: host.getAttribute('data-lib'),
          loop: false,
          recolour: true
        });
      }

      function playEmpty() {
        var host = document.querySelector('[data-mazo-empty-lottie]');
        if (!host || host.querySelector('svg')) return;   // absent, or already mounted
        var wrap = host.closest('[data-mazo-empty-illus]');
        /* Reduced motion keeps the inline SVG fallback and never fetches anything — the fallback IS the
           reduced-motion treatment, so there is nothing extra to build. */
        if (reduce || !wrap) return;
        mount('empty', host, {
          src: wrap.getAttribute('data-src'),
          lib: wrap.getAttribute('data-lib'),
          /* ⚠️ LOOPS, on owner request ("the new empty cart animation only - keep looping"). It is the only
             thing on screen in that state, so a single play leaves a still frame. The confetti does NOT
             loop — that one is a single reward. */
          loop: true,
          onReady: function () { wrap.setAttribute('data-mazo-lottie', 'on'); }
        });
      }

      /* Mount whatever anchors are present and unmounted. Called after EVERY drawer render, which is what
         makes the empty animation appear when the LAST ITEM IS REMOVED — the drawer is already open at that
         moment, so no open-transition fires and an open-only trigger silently did nothing. */
      function mountAnchors() {
        /* 2026-10-08 speed: never build Lottie inside a CLOSED drawer (it ran at every page load, ~1.7 s phone CPU on the PDP). Open -> watch() plays it. */
        var cd = document.querySelector('cart-drawer'); if (cd && !cd.classList.contains('active') && location.pathname.indexOf('/cart') !== 0) return;
        playEmpty();
        celebrate(false);   // guarded: mounts only if the anchor exists and is empty
      }

    window.mazoCartLottie = { celebrate: celebrate, playEmpty: playEmpty, mountAnchors: mountAnchors };
  })();
}
